import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { StarRating } from "@/components/StarRating";
import api from "@/lib/api";
import ReviewDialog from "@/components/ReviewDialog";
import {
  ShieldCheck, BadgeCheck, MapPin, Star, Wallet, MessagesSquare, ArrowRight,
  GraduationCap, Search, HandCoins, Quote, PenLine,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1505554709290-ee1e2aeabd1f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

function Step({ n, icon: Icon, title, text }) {
  return (
    <div className="relative pl-14" data-testid={`how-step-${n}`}>
      <div className="absolute left-0 top-0 h-10 w-10 rounded-lg bg-ink text-white grid place-items-center">
        <Icon size={18} />
      </div>
      <div className="label-mono text-slate-400 mb-1">Step {n}</div>
      <h4 className="font-display font-semibold text-ink text-lg">{title}</h4>
      <p className="text-sm text-slate-500 mt-1 leading-relaxed">{text}</p>
    </div>
  );
}

function ValueCard({ icon: Icon, title, text, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-[0_8px_30px_rgba(15,23,42,0.06)] transition-shadow">
      <div className="h-11 w-11 rounded-lg grid place-items-center mb-4" style={{ background: accent + "14", color: accent }}>
        <Icon size={20} />
      </div>
      <h4 className="font-display font-semibold text-ink text-lg">{title}</h4>
      <p className="text-sm text-slate-500 mt-2 leading-relaxed">{text}</p>
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();
  const { openAuth } = useOutletContext();
  const [reviews, setReviews] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(false);

  const loadReviews = () => api.get("/testimonials").then(({ data }) => setReviews(data)).catch(() => {});
  useEffect(() => { loadReviews(); }, []);

  const openReview = () => (user ? setReviewOpen(true) : openAuth());

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 grid-texture opacity-70" />
        <div className="relative mx-auto max-w-7xl px-4 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              <GraduationCap size={14} className="text-crimson" />
              Exclusive to SRM KTR students
            </div>
            <h1 className="mt-5 font-display font-extrabold tracking-tight text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Buy & sell on campus,<br />
              <span className="text-brand">with people you trust.</span>
            </h1>
            <p className="mt-5 text-slate-600 text-base sm:text-lg max-w-xl leading-relaxed">
              A verified, student-only marketplace inside SRM University Kattankulathur. Find affordable second-hand
              textbooks, gadgets, cycles and dorm essentials — and meet safely at campus pickup points.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {user ? (
                <Link to="/marketplace">
                  <Button className="bg-ink hover:bg-slate-800 text-white h-12 px-6 rounded-full text-sm" data-testid="hero-browse-btn">
                    Browse Marketplace <ArrowRight size={16} className="ml-1.5" />
                  </Button>
                </Link>
              ) : (
                <Button onClick={openAuth} className="bg-ink hover:bg-slate-800 text-white h-12 px-6 rounded-full text-sm" data-testid="hero-getstarted-btn">
                  Get started — it's your campus <ArrowRight size={16} className="ml-1.5" />
                </Button>
              )}
              <Link to="/membership">
                <Button variant="outline" className="h-12 px-6 rounded-full text-sm border-slate-300" data-testid="hero-membership-btn">
                  See Campus Pass · ₹29/mo
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-1.5"><ShieldCheck size={16} className="text-emerald-500" /> Verified students only</div>
              <div className="flex items-center gap-1.5"><Wallet size={16} className="text-brand" /> Zero commission</div>
            </div>
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: "0.1s" }}>
            <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-[0_24px_60px_rgba(15,23,42,0.16)]">
              <img src={HERO_IMG} alt="Student essentials" className="w-full h-[420px] object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-4 bg-white rounded-xl border border-slate-200 shadow-lg px-4 py-3 flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-emerald-50 grid place-items-center"><BadgeCheck size={18} className="text-emerald-500" /></div>
              <div>
                <div className="font-display font-bold text-ink text-sm leading-none">1,200+ students</div>
                <div className="text-xs text-slate-500 mt-0.5">trading on campus</div>
              </div>
            </div>
            <div className="absolute -top-4 -right-3 bg-ink text-white rounded-xl shadow-lg px-4 py-3">
              <div className="flex items-center gap-1"><StarRating value={5} size={13} /></div>
              <div className="text-xs mt-1 text-slate-300">4.9 avg seller rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-4 lg:px-8 py-16 lg:py-20">
        <div className="max-w-2xl">
          <div className="label-mono text-brand mb-2">How it works</div>
          <h2 className="font-display font-bold text-ink text-2xl sm:text-3xl lg:text-4xl tracking-tight">
            Three steps from clutter to cash
          </h2>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-10">
          <Step n={1} icon={GraduationCap} title="Verify your SRM email" text="Sign up with your @srmist.edu.in address. Only genuine SRM KTR students get in — no strangers, no scams." />
          <Step n={2} icon={Search} title="Buy or list an item" text="Browse by category or post your own listing in seconds. Chat with the other student to agree on a fair price." />
          <Step n={3} icon={MapPin} title="Meet at a campus spot" text="Hand off safely at a verified pickup point like Tech Park or UB Plaza, then rate each other." />
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="bg-white border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-4 lg:px-8 py-16 lg:py-20">
          <div className="grid md:grid-cols-3 gap-5">
            <ValueCard icon={MapPin} title="Safe on-campus handoffs" accent="#059669" text="Every deal is arranged at a predefined, high-traffic campus location — no risky off-campus meetups." />
            <ValueCard icon={HandCoins} title="Zero commission" accent="#2563EB" text="Keep 100% of your sale. Your ₹29/month Campus Pass is the only cost — no cuts on transactions." />
            <ValueCard icon={Star} title="Student rating system" accent="#D97706" text="Buyers and sellers rate each other after every deal, so the most trusted students rise to the top." />
          </div>
        </div>
      </section>

      {/* AUTH / CTA SECTION */}
      <section className="mx-auto max-w-7xl px-4 lg:px-8 py-16 lg:py-20">
        <div className="rounded-2xl bg-ink text-white overflow-hidden relative">
          <div className="absolute inset-0 grid-texture opacity-10" />
          <div className="relative px-6 sm:px-12 py-12 lg:py-16 grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="label-mono text-brand-100/70 mb-3 flex items-center gap-2"><MessagesSquare size={14} /> Join in 30 seconds</div>
              <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight">
                {user ? `Welcome back, ${user.name.split(" ")[0]}.` : "Ready to trade with your batchmates?"}
              </h2>
              <p className="mt-4 text-slate-300 max-w-md leading-relaxed">
                {user
                  ? "Head to the marketplace to find deals, or list something you no longer need."
                  : "Create your verified student account and unlock buying, selling and in-app chat across the SRM KTR campus."}
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              {user ? (
                <Link to="/marketplace">
                  <Button className="bg-white text-ink hover:bg-slate-100 h-12 px-6 rounded-full" data-testid="cta-marketplace-btn">
                    Go to Marketplace <ArrowRight size={16} className="ml-1.5" />
                  </Button>
                </Link>
              ) : (
                <>
                  <Button onClick={openAuth} className="bg-brand hover:bg-brand-700 text-white h-12 px-6 rounded-full" data-testid="cta-signup-btn">
                    Sign up with SRM email
                  </Button>
                  <Button onClick={openAuth} variant="outline" className="h-12 px-6 rounded-full border-white/25 bg-transparent text-white hover:bg-white/10" data-testid="cta-login-btn">
                    Log in
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-white border-t border-slate-200">
        <div className="mx-auto max-w-7xl px-4 lg:px-8 py-16 lg:py-20">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-2xl">
              <div className="label-mono text-brand mb-2">Student reviews</div>
              <h2 className="font-display font-bold text-ink text-2xl sm:text-3xl lg:text-4xl tracking-tight">
                Trusted by students across campus
              </h2>
              <p className="mt-3 text-slate-500">Real feedback on product quality and the platform experience.</p>
            </div>
            <Button onClick={openReview} variant="outline" className="border-slate-300 rounded-full h-11 px-5" data-testid="write-review-btn">
              <PenLine size={16} className="mr-1.5" /> Write a review
            </Button>
          </div>
          {reviews.length === 0 ? (
            <div className="mt-10 rounded-xl border border-dashed border-slate-300 bg-[#F8FAFC] p-12 text-center" data-testid="reviews-empty">
              <Quote size={26} className="mx-auto text-slate-300" />
              <p className="mt-3 text-slate-500 max-w-md mx-auto">No reviews yet. Once verified students complete deals and share feedback, their reviews will appear here.</p>
              <Button onClick={openReview} className="mt-5 bg-ink hover:bg-slate-800 text-white rounded-full h-11 px-5" data-testid="reviews-empty-cta">
                <PenLine size={16} className="mr-1.5" /> Be the first to review
              </Button>
            </div>
          ) : (
            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {reviews.map((r, i) => (
                <div key={r.id} className="bg-[#F8FAFC] border border-slate-200 rounded-xl p-6 flex flex-col animate-fade-up" style={{ animationDelay: `${i * 0.05}s` }} data-testid={`testimonial-${i}`}>
                  <Quote size={22} className="text-slate-300" />
                  <p className="mt-3 text-sm text-slate-700 leading-relaxed flex-1">"{r.text}"</p>
                  <div className="mt-5 flex items-center gap-3">
                    <img src={r.avatar} alt={r.name} className="h-10 w-10 rounded-full border border-slate-200" />
                    <div className="min-w-0">
                      <div className="font-semibold text-ink text-sm">{r.name}</div>
                      <div className="text-xs text-slate-500 truncate">{r.dept}</div>
                    </div>
                    <StarRating value={r.stars} size={12} className="ml-auto" />
                  </div>
                  {r.verified_purchase && (
                    <div className="mt-3 inline-flex items-center gap-1 self-start text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5" data-testid={`verified-purchase-${i}`}>
                      <ShieldCheck size={12} /> Verified purchase
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <ReviewDialog open={reviewOpen} onOpenChange={setReviewOpen} onSubmitted={loadReviews} />
    </div>
  );
}
